import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

export default function Settings() {
  const { user, updateUser } = useAuth();
  const [username, setUsername] = useState('');
  const [loading, setLoading]   = useState(false);
  const [msg, setMsg]           = useState('');
  const [err, setErr]           = useState('');

  // MT5 container card
  const [restarting, setRestarting] = useState(false);
  const [containerMsg, setContainerMsg] = useState('');
  const [containerErr, setContainerErr] = useState('');

  // MT5 credentials card
  const [mt5Account, setMt5Account]   = useState('');
  const [mt5Server, setMt5Server]     = useState('');
  const [mt5Password, setMt5Password] = useState('');
  const [credsSaving, setCredsSaving] = useState(false);
  const [credsMsg, setCredsMsg]       = useState('');
  const [credsErr, setCredsErr]       = useState('');

  useEffect(() => {
    setUsername(user?.username || '');
  }, [user?.username]);

  useEffect(() => {
    setMt5Account(user?.mt5AccountNumber || '');
    setMt5Server(user?.mt5Server || '');
  }, [user?.mt5AccountNumber, user?.mt5Server]);

  // The login response omits the MT5 fields — refresh the profile once so the
  // credentials card is populated even before a full page reload.
  useEffect(() => {
    api.get('/auth/me')
      .then(r => updateUser(r.data))
      .catch(() => {});
  }, []); // eslint-disable-line

  function sanitize(v) {
    return v.toLowerCase().replace(/[^a-z0-9_.-]/g, '');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setErr(''); setMsg('');
    if (!/^[a-z0-9_.-]{3,24}$/.test(username)) {
      setErr('Username must be 3–24 chars: lowercase letters, numbers, _ . -');
      return;
    }
    if (username === user?.username) {
      setMsg('No change.'); return;
    }
    setLoading(true);
    try {
      const r = await api.put('/auth/me', { username });
      updateUser({ username: r.data?.user?.username || username });
      setMsg('Username updated.');
    } catch (e2) {
      setErr(e2.response?.data?.error || 'Update failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleCredsSubmit(e) {
    e.preventDefault();
    setCredsErr(''); setCredsMsg('');

    const account = mt5Account.trim();
    const server  = mt5Server.trim();
    if (account && !/^\d{1,20}$/.test(account)) {
      setCredsErr('MT5 account number must be digits only.');
      return;
    }

    const payload = {};
    if (account !== (user?.mt5AccountNumber || '')) payload.mt5AccountNumber = account;
    if (server  !== (user?.mt5Server || ''))        payload.mt5Server = server;
    if (mt5Password)                                payload.mt5Password = mt5Password;
    if (Object.keys(payload).length === 0) {
      setCredsMsg('No change.');
      return;
    }

    if (user?.status === 'verified' && !window.confirm(
      'Saving new MT5 credentials restarts your terminal to apply them. ' +
      'Your deployed EAs will be offline for ~1–2 minutes and resume automatically. Continue?')) return;

    setCredsSaving(true);
    try {
      const r = await api.put('/auth/me', payload);
      updateUser({ mt5AccountNumber: account, mt5Server: server });
      setMt5Password('');
      setCredsMsg(r.data?.message || 'MT5 credentials updated.');
    } catch (e2) {
      setCredsErr(e2.response?.data?.error || 'Update failed');
    } finally {
      setCredsSaving(false);
    }
  }

  async function handleRestart() {
    if (!window.confirm(
      'Restart your MT5 terminal? Your deployed EAs will be offline for ~1–2 minutes and resume automatically.')) return;
    setContainerErr(''); setContainerMsg('');
    setRestarting(true);
    try {
      await api.post('/customer/container/restart');
      setContainerMsg('Restart initiated — your dashboard will update as the terminal comes back online.');
    } catch (e2) {
      setContainerErr(e2.response?.data?.error || 'Restart failed');
    } finally {
      setRestarting(false);
    }
  }

  return (
    <>
      <div className="ct-page-header">
        <div>
          <span className="eyebrow">Account</span>
          <h1>Settings</h1>
          <div className="subtitle">Your profile and MT5 terminal controls</div>
        </div>
      </div>

      <div className="row">
      <div className="col-lg-7 col-md-9 col-12">
        <div className="card">
          <div className="card-header">
            <h5 className="card-title mb-0">Account Settings</h5>
          </div>
          <div className="card-body">
            {!user?.username && (
              <div className="alert alert-info" style={{ fontSize: 13 }}>
                You don't have a username yet. Pick one — other users will see this
                instead of your email.
              </div>
            )}
            {msg && <div className="alert alert-success">{msg}</div>}
            {err && <div className="alert alert-danger">{err}</div>}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="text-white-50">Email</label>
                <input className="form-control" value={user?.email || ''} disabled />
                <small className="text-white-50">
                  Email is used to sign in and cannot be changed here.
                </small>
              </div>

              <div className="form-group">
                <label className="text-white-50">Username</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. og_trader"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck="false"
                  value={username}
                  onChange={e => setUsername(sanitize(e.target.value))}
                />
                <small className="text-white-50">
                  3–24 chars · lowercase letters, numbers, <code>_ . -</code>
                </small>
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || username === (user?.username || '')}
              >
                {loading ? 'Saving...' : 'Save username'}
              </button>
            </form>
          </div>
        </div>

        <div className="card mt-3">
          <div className="card-header">
            <h5 className="card-title mb-0">MT5 Credentials</h5>
            <small className="text-white-50">
              The broker account your EAs trade on.
            </small>
          </div>
          <div className="card-body">
            {credsMsg && <div className="alert alert-success">{credsMsg}</div>}
            {credsErr && <div className="alert alert-danger">{credsErr}</div>}

            <form onSubmit={handleCredsSubmit}>
              <div className="form-group">
                <label className="text-white-50">Account number</label>
                <input
                  type="text"
                  inputMode="numeric"
                  className="form-control"
                  placeholder="e.g. 12345678"
                  autoComplete="off"
                  value={mt5Account}
                  onChange={e => setMt5Account(e.target.value.replace(/[^\d]/g, ''))}
                />
              </div>

              <div className="form-group">
                <label className="text-white-50">Server</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. ICMarketsSC-MT5"
                  autoComplete="off"
                  spellCheck="false"
                  value={mt5Server}
                  onChange={e => setMt5Server(e.target.value)}
                />
                <small className="text-white-50">
                  Exactly as shown in your broker's welcome email / MT5 login dialog.
                </small>
              </div>

              <div className="form-group">
                <label className="text-white-50">MT5 password</label>
                <input
                  type="password"
                  className="form-control"
                  placeholder="Leave blank to keep current password"
                  autoComplete="new-password"
                  value={mt5Password}
                  onChange={e => setMt5Password(e.target.value)}
                />
                <small className="text-white-50">
                  Must be the master (trading) password — EAs can't trade with an
                  investor password.
                </small>
              </div>

              <p className="text-white-50" style={{ fontSize: 13 }}>
                Saving new credentials restarts your MT5 terminal so it logs in
                with them. Deployed EAs resume automatically.
              </p>

              <button type="submit" className="btn btn-primary" disabled={credsSaving}>
                {credsSaving ? 'Saving...' : 'Save MT5 credentials'}
              </button>
            </form>
          </div>
        </div>

        <div className="card mt-3">
          <div className="card-header">
            <h5 className="card-title mb-0">MT5 Terminal</h5>
            <small className="text-white-50">
              Your dedicated MT5 instance running your EAs 24/7.
            </small>
          </div>
          <div className="card-body">
            {containerMsg && <div className="alert alert-success">{containerMsg}</div>}
            {containerErr && <div className="alert alert-danger">{containerErr}</div>}

            <p className="text-white-50" style={{ fontSize: 13 }}>
              If an EA looks stuck or your terminal lost its broker connection,
              a restart usually fixes it. Deployed EAs reattach automatically.
            </p>
            <button
              type="button"
              className="btn btn-warning"
              onClick={handleRestart}
              disabled={restarting || user?.status !== 'verified'}
            >
              {restarting ? 'Restarting…' : 'Restart MT5 terminal'}
            </button>
          </div>
        </div>
      </div>
      </div>
    </>
  );
}
